/**
 * Centralized Pagination Configuration
 * Change pagination settings here to take effect across the entire application
 */

export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 75, 100],
  DEFAULT_PAGE: 0,
  SEARCH_DEBOUNCE_DELAY: 500,
  API_DEBOUNCE_DELAY: 100,
  MAX_PAGE_SIZE: 100,
  MIN_PAGE_SIZE: 0
};

/**
 * Utility functions for pagination
 */
export const PaginationUtils = {
  toBackendPage: (frontendPage) => frontendPage + 1,
  toFrontendPage: (backendPage) => Math.max(0, backendPage - 1),
  isValidPage: (page, totalItems, pageSize) => {
    if (totalItems === 0) return page === 0;
    const maxPage = Math.ceil(totalItems / pageSize) - 1;
    return page >= 0 && page <= maxPage;
  },

  getMaxPage: (totalItems, pageSize) => {
    if (totalItems === 0) return 0;
    return Math.ceil(totalItems / pageSize) - 1;
  },

  getSafePage: (requestedPage, totalItems, pageSize) => {
    const maxPage = PaginationUtils.getMaxPage(totalItems, pageSize);
    return Math.min(Math.max(0, requestedPage), maxPage);
  },

  getValidPageSize: (pageSize) => {
    if (PAGINATION_CONFIG.PAGE_SIZE_OPTIONS.includes(pageSize)) {
      return pageSize;
    }
    return PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
  }
  
};

/**
 * Cursor (keyset) pagination, used by endpoints that page by a "last id seen"
 * cursor rather than an offset — e.g. GET /searches/:companyid, which takes
 * `limit` and `sinceId`. The limits mirror the backend's CURSOR_PAGINATION_CONFIG.
 */
export const CURSOR_PAGINATION_CONFIG = {
  DEFAULT_LIMIT: 500,
  MIN_LIMIT: 1,
  // Anything larger is clamped by the backend, so asking for more is wasted.
  MAX_LIMIT: 500,
  // Backstop against a cursor that never reports the end of the list.
  MAX_PAGES: 500
};

/**
 * Utility functions for cursor pagination
 */
export const CursorPaginationUtils = {
  getValidLimit: (limit) => {
    const limitNum = parseInt(limit, 10);
    if (isNaN(limitNum)) {
      return CURSOR_PAGINATION_CONFIG.DEFAULT_LIMIT;
    }
    if (limitNum < CURSOR_PAGINATION_CONFIG.MIN_LIMIT) {
      return CURSOR_PAGINATION_CONFIG.MIN_LIMIT;
    }
    return Math.min(limitNum, CURSOR_PAGINATION_CONFIG.MAX_LIMIT);
  }
};

export default PAGINATION_CONFIG;
